import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/kit";
import { PlanBreathe, PlanSkill, PlanSupporter } from "@/components/plan/PlanSteps";
import { StreakMeter } from "@/components/plan/StreakMeter";
import { ThankButton } from "@/components/ThankButton";
import { logError } from "@/lib/log";

const TITLES = { skill: "Start with what helps", breathe: "One minute of breathing", supporter: "Lean on someone", done: "You're ready" };

const Thanks = ({ items }) => {
  const [done, setDone] = useState([]);
  if (!items?.length) return null;
  return (
    <div className="mm-soft p-3 mt-4 text-left space-y-2" data-testid="plan-thankable">
      <div className="text-sm font-semibold">Your supporters showed up for you today</div>
      {items.map((t) => {
        const k = `${t.note_id}-${t.index}`;
        return (
          <div key={k} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate"><b>{t.supporter_name}:</b> {t.message}</span>
            <ThankButton noteId={t.note_id} index={t.index} name={t.supporter_name} done={done.includes(k)} onDone={() => setDone((d) => [...d, k])} />
          </div>
        );
      })}
    </div>
  );
};

const Done = ({ plan, onFinish }) => (
  <div className="text-center" data-testid="plan-step-done">
    <CheckCircle2 size={48} className="mx-auto text-mm-primary" />
    <Thanks items={plan.thankable} />
    <p className="mt-3 text-sm text-mm-muted">Tough days pass. Be gentle with yourself today — you've already taken care of yourself once.</p>
    {plan.completed ? <Link to="/" className="mm-btn w-full mt-5 inline-flex justify-center" data-testid="plan-home">Back to home</Link>
      : <button className="mm-btn w-full mt-5" onClick={onFinish} data-testid="plan-finish">Done, I'm ready</button>}
  </div>
);

export default function ToughDayPlan() {
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    api.post("/tough-day-plan/open", { from_nudge: params.get("nudge") === "1" }).catch((e) => logError("Plan open failed:", e));
    api.get("/tough-day-plan").then((r) => setPlan(r.data)).catch((e) => { toast.error(errMsg(e)); setPlan(false); });
  }, [params]);

  const finish = async () => {
    try {
      const { data } = await api.post("/tough-day-plan/complete");
      setPlan((p) => ({ ...p, completed: true, streak: data.streak }));
      if (data.badge) toast.success(`${data.badge.icon} Badge earned: ${data.badge.name} — ${data.badge.days} tough days in a row!`, { duration: 6000 });
      else toast.success(data.plan_streak > 1 ? `Plan done — ${data.plan_streak} nudge days in a row 💜` : "Plan done. You've got this 💜");
    }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (plan === null) return <Spinner />;
  if (!plan) return null;
  const steps = [...plan.steps, "done"];
  const key = steps[step];
  const next = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  return (
    <div className="max-w-lg" data-testid="tough-day-plan">
      <PageHeader back title="Plan for today" subtitle={`${plan.weekday} · about 2 minutes`} />
      <StreakMeter streak={plan.streak} />
      <div className="flex gap-1.5 mb-5" data-testid="plan-progress">
        {steps.map((k, i) => <button key={k} onClick={() => setStep(i)} aria-label={TITLES[k]} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-mm-primary" : "bg-mm-soft"}`} data-testid={`plan-progress-${i}`} />)}
      </div>
      <div className="mm-card p-6 mm-fade-up" key={step}>
        <div className="text-xs font-semibold text-mm-primary uppercase tracking-wider">Step {step + 1} of {steps.length}</div>
        <h2 className="font-display text-xl font-bold mt-1 mb-3" data-testid="plan-step-title">{TITLES[key]}</h2>
        {key === "skill" && <PlanSkill best={plan.best_skill} chosenId={plan.skill_id} onNext={next} />}
        {key === "breathe" && <PlanBreathe onNext={next} />}
        {key === "supporter" && <PlanSupporter plan={plan} onNext={next} />}
        {key === "done" && <Done plan={plan} onFinish={finish} />}
      </div>
    </div>
  );
}
